import { CornerDownLeft, Search } from "lucide-react";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface AskBarProps {
  onAsk: (requestText: string) => void;
  isBusy: boolean;
  /** Shown only on the opening screen, where they teach what the tool accepts. */
  examples?: string[];
  autoFocus?: boolean;
  placeholder?: string;
}

/**
 * The only input in the tool.
 *
 * The requestor describes what they need; the composer works out what it can do
 * about it. There is deliberately nothing else to configure here — no asset
 * picker, no mode switch, no edit-type menu.
 */
export function AskBar({
  onAsk,
  isBusy,
  examples,
  autoFocus = false,
  placeholder = "Describe what you need — a deck, an image, a document in another language",
}: AskBarProps) {
  const [value, setValue] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);

  /**
   * Put a starter in the box for the requestor to finish.
   *
   * Not submitted. The examples used to be whole requests naming the two PDFs
   * the library happened to hold, so clicking one asked a question about
   * material that may not be there — and taught that the tool wants a
   * complete, precisely-worded sentence. They are openings now, so the click
   * has to leave the cursor after the last word rather than fire a half-formed
   * ask.
   */
  const start = (opening: string) => {
    setValue(opening);
    const node = field.current;
    if (!node) {
      return;
    }
    node.focus();
    // After the state has landed, or the caret sits at the old length.
    requestAnimationFrame(() => node.setSelectionRange(opening.length, opening.length));
  };

  const submit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isBusy) {
      return;
    }
    onAsk(trimmed);
    setValue("");
  };

  return (
    <div className="space-y-3">
      <div
        className={cn(
          "border-border bg-card focus-within:border-velux/40 focus-within:ring-velux/10",
          "rounded-2xl border shadow-sm transition focus-within:ring-4",
        )}
      >
        <Textarea
          ref={field}
          value={value}
          autoFocus={autoFocus}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            // ⌘/Ctrl+Enter submits; plain Enter keeps a newline so a longer
            // request can be written out properly.
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              submit(value);
            }
          }}
          rows={2}
          placeholder={placeholder}
          aria-label="What asset are you looking for?"
          className="min-h-[64px] resize-none border-0 bg-transparent px-4 py-3 text-base shadow-none focus-visible:ring-0"
        />
        <div className="border-border flex items-center justify-between gap-3 border-t px-3 py-2">
          <p className="text-muted flex items-center gap-1.5 text-xs">
            <CornerDownLeft className="h-3 w-3" />
            <span className="hidden sm:inline">⌘ + Enter to send</span>
          </p>
          <Button onClick={() => submit(value)} disabled={isBusy || !value.trim()} size="sm">
            <Search className="mr-1.5 h-3.5 w-3.5" />
            {isBusy ? "Looking…" : "Ask"}
          </Button>
        </div>
      </div>

      {examples && examples.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {examples.map((example) => (
            <button
              key={example}
              type="button"
              disabled={isBusy}
              onClick={() => start(example)}
              className={cn(
                "border-border text-muted hover:border-velux/40 hover:text-foreground",
                "rounded-full border bg-transparent px-3 py-1.5 text-left text-xs transition",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              {example}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
